import React, { useEffect } from 'react';
import Navigation from './Navigation';
import Footer from './Footer';
import TermsContent from './TermsContent';
import PageSEO from './PageSEO'; // Import the SEO component

const TermsOfUse = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // SEO configuration for terms of use page
  const termsSEO = {
    title: "Terms of Use | Kinder's Meats Deli BBQ & Catering",
    description: "Terms of Use for Kinder's Meats Deli BBQ & Catering. Review the terms and conditions governing your use of our website, services, and restaurant locations.",
    keywords: [
      "Kinder's Meats terms of use",
      "restaurant terms of service",
      "website terms and conditions",
      "service terms",
      "restaurant policies",
      "user agreement",
      "terms and conditions California",
      "restaurant service agreement"
    ],
    canonicalUrl: "/terms-of-use",
    noIndex: false, // Terms should be indexed for compliance
    ogImage: "https://kindersmeats.com/images/kinders-logo-og.jpg",
    customMeta: {
      'document:type': 'Legal Document',
      'document:category': 'Terms of Service',
      'document:effective-date': 'February 9, 2026',
      'robots': 'index, follow, noarchive' // Don't cache sensitive legal content
    },
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://kindersmeats.com/terms-of-use#webpage",
          "url": "https://kindersmeats.com/terms-of-use",
          "name": "Terms of Use | Kinder's Meats Deli BBQ & Catering",
          "description": "Terms of Use for Kinder's Meats Deli BBQ & Catering governing website use and restaurant services.",
          "isPartOf": {
            "@id": "https://kindersmeats.com/#website"
          },
          "about": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "datePublished": "2025-06-04",
          "dateModified": "2026-02-09",
          "inLanguage": "en-US",
          "breadcrumb": {
            "@id": "https://kindersmeats.com/terms-of-use#breadcrumb"
          },
          "mainEntity": {
            "@id": "https://kindersmeats.com/terms-of-use#termsofuse"
          }
        },
        {
          "@type": "DigitalDocument",
          "@id": "https://kindersmeats.com/terms-of-use#termsofuse",
          "name": "Kinder's Meats Terms of Use",
          "description": "Legal agreement governing the use of Kinder's Meats services, website, and restaurant locations",
          "dateCreated": "2025-06-04",
          "dateModified": "2026-02-09",
          "author": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "publisher": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "genre": "Legal Document",
          "keywords": "terms of use, terms of service, user agreement, restaurant policies, service conditions",
          "inLanguage": "en-US",
          "copyrightHolder": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "license": "All rights reserved",
          "about": [
            {
              "@type": "Thing",
              "name": "Website Use",
              "description": "Terms governing website access and usage"
            },
            {
              "@type": "Thing",
              "name": "Restaurant Services",
              "description": "Terms and conditions for restaurant services"
            },
            {
              "@type": "Thing",
              "name": "User Responsibilities",
              "description": "Customer obligations and responsibilities"
            },
            {
              "@type": "Thing",
              "name": "Limitation of Liability",
              "description": "Legal limitations and disclaimers"
            },
            {
              "@type": "Thing",
              "name": "Intellectual Property",
              "description": "Rights to trademarks, content, and materials"
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://kindersmeats.com/#organization",
          "name": "Kinder's Meats Deli BBQ & Catering",
          "url": "https://kindersmeats.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://kindersmeats.com/images/kinders-logo.png",
            "width": 300,
            "height": 100
          },
          "description": "Family-owned BBQ restaurant chain serving authentic barbecue across 7 California locations since 1946.",
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "CA",
            "addressCountry": "US"
          },
          "termsOfService": "https://kindersmeats.com/terms-of-use",
          "privacyPolicy": "https://kindersmeats.com/privacy-policy"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://kindersmeats.com/terms-of-use#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://kindersmeats.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Terms of Use",
              "item": "https://kindersmeats.com/terms-of-use"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What do these terms of use cover?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "These terms govern your use of Kinder's Meats website, restaurant services, online ordering, and any interactions with our business. By using our services, you agree to these terms."
              }
            },
            {
              "@type": "Question",
              "name": "Can I use content from your website?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "All website content, including text, images, logos, and trademarks, are owned by Kinder's Meats. Personal use is permitted, but commercial use requires written permission."
              }
            },
            {
              "@type": "Question",
              "name": "What are my responsibilities as a customer?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Customers must provide accurate information, respect our staff and property, follow restaurant policies, and use our services in accordance with applicable laws."
              }
            },
            {
              "@type": "Question",
              "name": "How do you handle disputes or complaints?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We encourage customers to contact us directly to resolve issues. For formal disputes, these terms outline the resolution process and applicable jurisdiction."
              }
            },
            {
              "@type": "Question",
              "name": "Can these terms change?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, we may update these terms periodically. Changes will be posted on this page with an updated effective date. Continued use of our services indicates acceptance of revised terms."
              }
            },
            {
              "@type": "Question",
              "name": "What if I don't agree with these terms?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "If you don't agree with these terms, you should discontinue use of our website and services. Contact us if you have specific concerns about any provisions."
              }
            }
          ]
        }
      ]
    }
  };
    
  return (
    <>
      {/* SEO Component */}
      <PageSEO {...termsSEO} />
      
      {/* Main content with semantic HTML structure */}
      <main>
        <section className="theme-light section m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column is-justify-content-start'>
            <Navigation 
              colorMode="colorWhite"
            />
            
            {/* Terms of Use Content */}
            <article itemScope itemType="https://schema.org/DigitalDocument">
              <TermsContent 
                className="content-space-160 is-10-mobile is-10-tablet is-10-desktop is-8-widescreen is-8-fullhd is-offset-1-mobile is-offset-1-tablet is-offset-1-desktop is-offset-2-widescreen is-offset-2-fullhd"
                title="Terms of Use"
                effectiveDate="February 9, 2026"
              />
            </article>

            {/* Hidden FAQ Section for SEO */}
            <section className="faq-section" style={{ display: 'none' }} aria-hidden="true" itemScope itemType="https://schema.org/FAQPage">
              <h2>Terms of Use FAQ</h2>
              
              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">What do these terms of use cover?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    These terms govern your use of Kinder's Meats website, restaurant services, online ordering, and any interactions with our business. By using our services, you agree to these terms.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">Can I use content from your website?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    All website content, including text, images, logos, and trademarks, are owned by Kinder's Meats. Personal use is permitted, but commercial use requires written permission.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">What are my responsibilities as a customer?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    Customers must provide accurate information, respect our staff and property, follow restaurant policies, and use our services in accordance with applicable laws.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">How do you handle disputes or complaints?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    We encourage customers to contact us directly to resolve issues. For formal disputes, these terms outline the resolution process and applicable jurisdiction.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">Can these terms change?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    Yes, we may update these terms periodically. Changes will be posted on this page with an updated effective date. Continued use of our services indicates acceptance of revised terms.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">What if I don't agree with these terms?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    If you don't agree with these terms, you should discontinue use of our website and services. Contact us if you have specific concerns about any provisions.
                  </div>
                </div>
              </div>
            </section>
            
            <Footer />
          </div>
        </section>
      </main>
    </>
  );
};

export default TermsOfUse;